import { ReactNode, useEffect, useMemo } from 'react';
import { useSearchContext } from '../SearchContext';
import { PossibleSectionInDependencies } from '../types';
import { useFeatureToggle } from '../../../../../../feature-flags/useFeatureToggle';
import { addHighlights, removeHighlights } from './sectionHighlightHelpers';

interface HighlightedSectionProps {
  children: ReactNode;

  section: Omit<PossibleSectionInDependencies, 'dependencies' | 'usedBy' | 'configurations'>;
  highlightEntireRow?: boolean;
}

export const HighlightedSection = ({
  children,
  section,
  highlightEntireRow = false,
}: HighlightedSectionProps) => {
  const { searchInDocumentationCard } = useFeatureToggle();
  const { searchTerm, searchResults, arrowNavigationMatchIdx } = useSearchContext();

  const sectionMatches = useMemo(() => {
    return searchResults.filter((result) => result.section === section);
  }, [searchResults, section]);

  const currentMatch = searchResults[arrowNavigationMatchIdx];

  useEffect(() => {
    if (!searchTerm || sectionMatches.length === 0) {
      removeHighlights(section);
      return;
    }

    addHighlights(
      section,
      searchTerm,
      sectionMatches,
      currentMatch?.matchIndex,
      highlightEntireRow,
      searchInDocumentationCard,
    );

    return () => removeHighlights(section);
  }, [
    searchTerm,
    sectionMatches,
    currentMatch?.matchIndex,
    section,
    highlightEntireRow,
    searchInDocumentationCard,
  ]);

  return <>{children}</>;
};
